import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '../context/TelegramContext';
import { useHaptic } from '../hooks/useHaptic';
import { Crown, FileText, Image, History, Zap, Check } from 'lucide-react';
import axios from 'axios';
import { subscriptionAPI } from '../services/api';
import './UpgradePage.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

function UpgradePage() {
  const navigate = useNavigate();
  const { webApp } = useTelegram();
  const haptic = useHaptic();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState('click');

  // Загрузка текущей подписки и лимитов
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await subscriptionAPI.getStatus();
        setSubscription(response.data.subscription);
        setLimits(response.data.limits);
      } catch (error) {
        console.error('Ошибка загрузки подписки:', error);
      } finally {
        setLoadingSubscription(false);
      }
    };

    fetchSubscription();
  }, []);


  // Загрузка тарифных планов из API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API_URL}/subscription/plans`);
        const plansData = response.data.plans.map(plan => {
          // Форматируем длительность
          let durationText;
          if (plan.duration_days === 365 || plan.duration_days === 366) {
            durationText = '1 год';
          } else if (plan.duration_days === 30) {
            durationText = '30 дней';
          } else if (plan.duration_days === 1) {
            durationText = '1 день';
          } else if (plan.duration_days < 5) {
            durationText = `${plan.duration_days} дня`;
          } else {
            durationText = `${plan.duration_days} дней`;
          }
          
          return {
            id: plan.plan_code,
            name: plan.name,
            description: plan.description,
            documents: plan.documents_limit,
            price: plan.price,
            currency: plan.currency === 'UZS' ? 'сум' : plan.currency === 'RUB' ? '₽' : plan.currency,
            duration: durationText,
            duration_days: plan.duration_days, // Сохраняем для сортировки
            popular: plan.sort_order === 1, // Популярный - со вторым по порядку
          };
        });
        
        // Сортируем: сначала месячные тарифы, потом годовые (по длительности)
        const sortedPlans = plansData.sort((a, b) => a.duration_days - b.duration_days);
        setPlans(sortedPlans);
      } catch (error) {
        console.error('Ошибка загрузки тарифов:', error);
        // Fallback на дефолтные тарифы
        setPlans([
          {
            id: 'basic_100',
            name: '100 документов',
            documents: 100,
            price: 25000,
            currency: 'сум',
            duration: '30 дней',
            popular: false,
          },
          {
            id: 'basic_200',
            name: '200 документов',
            documents: 200,
            price: 35000,
            currency: 'сум',
            duration: '30 дней',
            popular: true,
          },
          {
            id: 'basic_500',
            name: '500 документов',
            documents: 500,
            price: 50000,
            currency: 'сум',
            duration: '30 дней',
            popular: false,
          },
        ]);
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  // Функция для polling статуса платежа
  const startPaymentStatusPolling = (transactionId, initData) => {
    // Очищаем предыдущий интервал если есть
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await axios.get(
          `${API_URL}/payments/status/${transactionId}`,
          {
            headers: {
              'x-telegram-init-data': initData,
            },
          }
        );
        
        if (statusResponse.data.status === 'completed') {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setLoading(false);
          setSelectedPlan(null);
          setYookassaToken(null);
          
          // Показываем успех и обновляем страницу
          alert('✅ Оплата успешна! Подписка активирована.');
          window.location.reload();
        } else if (statusResponse.data.status === 'failed' || statusResponse.data.status === 'cancelled') {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setLoading(false);
          setSelectedPlan(null);
          setYookassaToken(null);
          alert('❌ Оплата отменена');
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
      }
    }, 3000);
    
    // Останавливаем проверку через 10 минут
    setTimeout(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setLoading(false);
        setSelectedPlan(null);
        setYookassaToken(null);
      }
    }, 600000);
  };

  // Очистка интервала при размонтировании
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleSelectPlan = async (plan) => {
    haptic.buttonPressImportant();
    setSelectedPlan(plan.id);
    setLoading(true);

    try {
      const initData = window.Telegram?.WebApp?.initData;
      console.log('InitData:', initData);
      
      if (!initData) {
        alert('Ошибка: Telegram WebApp не инициализирован. initData пустой.');
        setLoading(false);
        setSelectedPlan(null);
        return;
      }
      
      const response = await axios.post(
        `${API_URL}/payments/create`,
        {
          planType: plan.id,
          provider: selectedProvider,
        },
        {
          headers: {
            'x-telegram-init-data': initData,
          },
        }
      );

      // Проверяем наличие paymentUrl или confirmationToken
      if (response.data.paymentUrl || response.data.confirmationToken) {
        const transactionId = response.data.transaction.transaction_id;
        
        console.log('Payment response:', {
          provider: selectedProvider,
          confirmationToken: response.data.confirmationToken,
          paymentUrl: response.data.paymentUrl
        });
        
        // Для YooKassa переходим на отдельную страницу оплаты
        if (selectedProvider === 'yookassa' && response.data.confirmationToken) {
          console.log('Navigating to payment page with token:', response.data.confirmationToken);
          navigate('/payment', {
            state: {
              confirmationToken: response.data.confirmationToken,
              transactionId: transactionId
            }
          });
        } else if (selectedProvider === 'click') {
          // Для Click переходим на страницу ввода карты
          console.log('Navigating to Click payment page');
          navigate('/payment/click', {
            state: {
              planType: plan.id,
              planName: plan.name,
              amount: plan.price,
              transactionId: transactionId
            }
          });
        } else if (response.data.paymentUrl) {
          // Для Payme открываем браузер
          console.log('Opening payment URL in browser:', response.data.paymentUrl);
          if (webApp) {
            webApp.openLink(response.data.paymentUrl);
          } else {
            window.open(response.data.paymentUrl, '_blank');
          }
          
          // Начинаем проверку статуса платежа каждые 3 секунды
          startPaymentStatusPolling(transactionId, initData);
        }
      }
    } catch (error) {
      console.error('Payment creation error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      
      const errorMsg = error.response?.data?.error || error.message || 'Ошибка создания платежа';
      alert(`Ошибка: ${errorMsg}`);
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const isPremium = limits?.subscription?.isPremium;
  const isActive = subscription && subscription.status === 'active' && new Date(subscription.expires_at) > new Date();
  
  // Фильтруем тарифы: убираем текущий активный тариф из списка
  const availablePlans = isPremium && subscription 
    ? plans.filter(plan => plan.id !== subscription.type)
    : plans;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  if (loadingSubscription || loadingPlans) {
    return (
      <div className="upgrade-page container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <div style={{ textAlign: 'center' }}>
          <span className="loader"></span>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="upgrade-page container">
      {/* Текущая подписка */}
      {isPremium && isActive ? (
        <div className="current-subscription-section animate-fade-in">
          <div className="hero-icon premium">
            <Crown size={48} />
          </div>
          <h1 className="hero-title">Premium подписка</h1>
          <div className="subscription-status-card">
            <div className="status-row">
              <span className="status-label">План:</span>
              <span className="status-value">{limits.subscription.plan}</span>
            </div>
            <div className="status-row">
              <span className="status-label">Документов использовано:</span>
              <span className="status-value">
                {subscription.documents_used} из {subscription.documents_limit}
              </span>
            </div>
            <div className="status-row">
              <span className="status-label">Истекает:</span>
              <span className="status-value">
                {formatDate(subscription.expires_at)}
                <span className="days-remaining"> (через {getDaysRemaining(subscription.expires_at)} дн.)</span>
              </span>
            </div>
          </div>

          <div className="benefits-section">
            <h3 className="section-title">Ваши преимущества:</h3>
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <FileText size={24} />
              </div>
              <div className="feature-content">
                <h4>Лимит документов: {subscription.documents_limit}</h4>
                <p>Осталось {subscription.documents_limit - subscription.documents_used} документов</p>
              </div>
              <Check size={20} className="feature-check" />
            </div>

            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <Image size={24} />
              </div>
              <div className="feature-content">
                <h4>Безлимитные печати и подписи</h4>
                <p>Загружайте любое количество печатей и подписей</p>
              </div>
              <Check size={20} className="feature-check" />
            </div>

            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <History size={24} />
              </div>
              <div className="feature-content">
                <h4>Полная история</h4>
                <p>Доступ ко всем подписанным документам</p>
              </div>
              <Check size={20} className="feature-check" />
            </div>

            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <Zap size={24} />
              </div>
              <div className="feature-content">
                <h4>Приоритетная поддержка</h4>
                <p>Быстрые ответы на ваши вопросы</p>
              </div>
              <Check size={20} className="feature-check" />
            </div>
          </div>

          <div className="renewal-section">
            <h3 className="section-title">Хотите продлить или изменить план?</h3>
            <p className="section-subtitle">Выберите новый тарифный план</p>
          </div>
        </div>
      ) : (
        <div className="hero-section animate-fade-in">
          <div className="hero-icon">
            <Crown size={48} />
          </div>
          <h1 className="hero-title">Выберите тариф</h1>
          <p className="hero-subtitle">Подпишитесь и получите доступ ко всем возможностям</p>
        </div>
      )}

      {/* Преимущества - показываем только если нет активной подписки */}
      {!isPremium && (
        <div className="features-section animate-slide-up animate-delay-100">
          <h3 className="section-title">Что входит:</h3>
          
          <div className="feature-card stagger-item" style={{ animationDelay: '0.2s' }}>
            <div className="feature-icon-wrapper">
              <Image size={24} />
            </div>
            <div className="feature-content">
              <h4>Безлимитные печати и подписи</h4>
              <p>Загружайте любое количество печатей и подписей</p>
            </div>
            <Check size={20} className="feature-check" />
          </div>

          <div className="feature-card stagger-item" style={{ animationDelay: '0.3s' }}>
            <div className="feature-icon-wrapper">
              <History size={24} />
            </div>
            <div className="feature-content">
              <h4>Полная история</h4>
              <p>Доступ ко всем подписанным документам</p>
            </div>
            <Check size={20} className="feature-check" />
          </div>

          <div className="feature-card stagger-item" style={{ animationDelay: '0.4s' }}>
            <div className="feature-icon-wrapper">
              <Zap size={24} />
            </div>
            <div className="feature-content">
              <h4>Приоритетная поддержка</h4>
              <p>Быстрые ответы на ваши вопросы</p>
            </div>
            <Check size={20} className="feature-check" />
          </div>
        </div>
      )}

      {/* Выбор платежной системы */}
      {availablePlans.length > 0 && (
        <div className="payment-provider-section animate-slide-up animate-delay-400">
          <h3 className="section-title">Способ оплаты:</h3>
          <div className="provider-buttons">
            <button
              className={`provider-button ${selectedProvider === 'click' ? 'active' : ''}`}
              onClick={() => {
                haptic.buttonPress();
                setSelectedProvider('click');
              }}
            >
              <span className="provider-name">Click</span>
              <span className="provider-info">Узбекистан 🇺🇿</span>
            </button>
            {/* Payme временно скрыт */}
            {/* <button
              className={`provider-button ${selectedProvider === 'payme' ? 'active' : ''}`}
              onClick={() => {
                haptic.buttonPress();
                setSelectedProvider('payme');
              }}
            >
              <span className="provider-name">Payme</span>
              <span className="provider-info">Узбекистан 🇺🇿</span>
            </button> */}
            <button
              className={`provider-button ${selectedProvider === 'yookassa' ? 'active' : ''}`}
              onClick={() => {
                haptic.buttonPress();
                setSelectedProvider('yookassa');
              }}
            >
              <span className="provider-name">ЮKassa</span>
              <span className="provider-info">Россия 🇷🇺</span>
            </button>
          </div>
        </div>
      )}

      {/* Тарифные планы */}
      <div className="pricing-section animate-slide-up animate-delay-500">
        {availablePlans.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              {isPremium ? 'У вас уже максимальный тариф!' : 'Тарифы временно недоступны'}
            </p>
          </div>
        ) : (
          availablePlans.map((plan) => (
            <div
              key={plan.id}
              className={`price-card ${plan.popular ? 'featured' : ''}`}
            >
              {plan.popular && <div className="badge">Популярный</div>}
              <div className="price-header">
                <h3>{plan.name}</h3>
                {plan.description && <p className="plan-description">{plan.description}</p>}
                <div className="price">
                  <span className="amount">{plan.price.toLocaleString()}</span>
                  <span className="currency">{plan.currency}</span>
                </div>
                <p className="price-note">{plan.duration}</p>
                <p className="documents-count">{plan.documents} документов</p>
              </div>
              <button
                className={`button ${loading && selectedPlan === plan.id ? 'button-loading' : ''}`}
                onClick={() => handleSelectPlan(plan)}
                disabled={loading && selectedPlan === plan.id}
              >
                <span className="button-label">
                  {isPremium ? 'Изменить план' : 'Оформить подписку'}
                </span>
                {loading && selectedPlan === plan.id && <span className="loader" aria-hidden="true"></span>}
              </button>
            </div>
          ))
        )}
      </div>

      {/* Бесплатный план - показываем только если нет активной подписки */}
      {!isPremium && (
        <div className="free-plan-section animate-fade-in animate-delay-700">
          <h3>Бесплатный план</h3>
          <div className="free-plan-features">
            <span>• 3 документа</span>
            <span>• 1 печать</span>
            <span>• 1 подпись</span>
          </div>
          <span className="current-plan">Текущий план</span>
        </div>
      )}
    </div>
  );
}

export default UpgradePage;

